import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import {
  unitsFailed,
  unitsLoaded,
  unitsRequested,
} from "@src/reducers/units-slice";
import { getUnitsUseCase } from "@src/cloud-connect/timeseries/use-cases/get-units-use-case";

/**
 * Loads the units catalog once per sign-in, so every consumer (dashboard
 * widgets, the widget drawer and anything added later) can resolve unit ids
 * from the slice instead of calling the endpoint itself.
 *
 * The widget drawer keeps its own guarded request, which stays quiet as long
 * as this one has already put the catalog in the slice.
 */
export const useLoadUnits = () => {
  const dispatch = useDispatch();
  const authContext = useSelector(
    (state: RootState) => state.yourSlice.authContext,
  );
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (!authContext) {
      // A new sign-in may mean a different namespace, so allow one attempt.
      attemptedRef.current = false;
      return;
    }
    // One attempt per sign-in. A failure is retried on the next sign-in (the
    // guard resets when the auth context disappears), never in a loop, so a
    // dead endpoint can't spin here.
    //
    // The guard is a ref rather than the slice's `status` on purpose: this
    // hook runs at the app root, and selecting the status there would
    // re-render the whole app shell twice per app load for no benefit.
    if (attemptedRef.current) return;
    attemptedRef.current = true;

    dispatch(unitsRequested());
    getUnitsUseCase()
      .then((result) => {
        if (result.success && result.data) {
          dispatch(unitsLoaded(result.data));
        } else {
          dispatch(unitsFailed(result.error ?? "Failed to fetch units"));
        }
      })
      .catch((error) => {
        // Kept out of the "loading" state, which would otherwise block every
        // later attempt (including after the next sign-in).
        dispatch(
          unitsFailed(
            error instanceof Error ? error.message : "Failed to fetch units",
          ),
        );
      });
  }, [authContext, dispatch]);
};
